import { useEffect, useRef, useState, type RefObject } from "react";
import { faceForLine, type useFace } from "@softmax/avatar/expressions";
import { RemarkScheduler } from "./remark-scheduler";
import { type Remark } from "./page-remarks";
import type { CogentLive, PageContext } from "../chat/chat-view";
import type { useWidgetNavigation } from "../use-widget-navigation";
type Navigation = ReturnType<typeof useWidgetNavigation>;
export function useWidgetNotices({
  live,
  pageContext,
  cogentId,
  navigation,
  playFace,
}: {
  readonly live: CogentLive | null;
  readonly pageContext: PageContext | null;
  readonly cogentId: string | null;
  readonly navigation: Navigation;
  readonly playFace: ReturnType<typeof useFace>["play"];
}) {
  const { open, log: logRef } = navigation;
  const [remark, setRemark] = useState<Remark | null>(null);
  const scheduler = useRef<RemarkScheduler | null>(null);
  const previousPage = useRef<{
    path: string | undefined;
    facts: string;
  } | null>(null);
  useEffect(() => {
    const current = new RemarkScheduler({ onChange: setRemark });
    scheduler.current = current;
    previousPage.current = null;
    return () => current.dispose();
  }, []);
  useEffect(() => {
    const facts = JSON.stringify(pageContext);
    const previous = previousPage.current;

    if (!previous || previous.path !== pageContext?.path)
      activeScheduler(scheduler).visit(pageContext);
    else if (previous.facts !== facts)
      activeScheduler(scheduler).update(pageContext);

    previousPage.current = { path: pageContext?.path, facts };
  }, [pageContext]);
  useEffect(
    () => activeScheduler(scheduler).pause({ paused: !cogentId || open }),
    [cogentId, open],
  );
  useEffect(() => {
    if (remark) playFace(faceForLine(remark.text), 2400);
  }, [remark, playFace]);

  const heard = useRef<number | null>(null);
  useEffect(() => {
    if (!live?.loaded) return;

    const said = live.messages;

    const newest = said.at(-1);

    if (
      heard.current !== null &&
      said.length > heard.current &&
      newest?.from === "cogent"
    )
      playFace(faceForLine(newest.text), 2400);

    heard.current = said.length;
  }, [live?.loaded, live?.messages, playFace]);
  const newest = live?.messages.at(-1);
  const newestId = newest?.id;
  const pending = newest?.pending;
  const draft = live?.draft;
  useEffect(() => {
    if (!open && !newestId && !pending && !draft) return;

    const log = logRef.current;

    if (log) log.scrollTop = log.scrollHeight;
  }, [newestId, pending, draft, open, logRef]);
  return {
    remark,
    holdRemark: (hold: boolean) =>
      activeScheduler(scheduler).hold({ on: hold }),
    dismissRemark: () => activeScheduler(scheduler).dismiss(),
  };
}

function activeScheduler(
  ref: RefObject<RemarkScheduler | null>,
): RemarkScheduler {
  const scheduler = ref.current;

  if (scheduler === null)
    throw new Error("The Cogent notices are not mounted.");

  return scheduler;
}
